import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useAuthenticatedUser } from '@treams/treams-hooks';
import { GridIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Container, Empty, Jumbo, Separator, Skeleton } from '@treams/treams-ui';
import { Card, Subheading } from '@treams/treams-ui';

import TalentReviewGroupCard from './components/TalentReviewGroupCard';
import useBatchedTalentReviewGroups from './hooks/useBatchedTalentReviewGroups';
import { groupTalentReviewIndexMoments } from './talentReviewIndexMoments';

export default function TalentReviews() {
	const { t } = useTranslation();

	const authenticatedUser = useAuthenticatedUser();

	const allGroups = useBatchedTalentReviewGroups();

	// The viewer's own cycle moments, matched to groups client-side: one
	// request instead of `Group.talentReviewCycleMoment` per card.
	const momentsQuery = graphql.useTalentReviewIndexMomentsQuery({
		variables: { userID: authenticatedUser.id },
		errorPolicy: 'all',
	});

	const momentsByGroup = useMemo(
		() => groupTalentReviewIndexMoments(momentsQuery.data?.user?.moments ?? []),
		[momentsQuery.data?.user?.moments],
	);

	const groups = useMemo(
		() =>
			(allGroups ?? [])
				.filter((group) => group.viewerCanViewTalentReviews)
				.sort((a, b) => a.name.localeCompare(b.name)),
		[allGroups],
	);

	const loading = !allGroups;

	return (
		<>
			<Jumbo
				title={t('talentReviews.v2.index.title')}
				description={t('talentReviews.v2.index.description')}
				footer={
					<Container>
						<Separator />
					</Container>
				}
			/>

			<Container mt={4}>
				{loading ? (
					<Box mt={4}>
						<Subheading.Skeleton width={220} />
						<Card mt={3} p={0}>
							<Skeleton.Repeat count={6}>
								<TalentReviewGroupCard.Skeleton />
							</Skeleton.Repeat>
						</Card>
					</Box>
				) : groups.length > 0 ? (
					<Box mt={4}>
						<Subheading>{t('talentReviews.v2.index.groupsTitle')}</Subheading>
						<Card mt={3} p={0}>
							{groups.map((group) => (
								<TalentReviewGroupCard
									key={group.id}
									group={group}
									cycleMoment={momentsByGroup.get(group.id)}
								/>
							))}
						</Card>
					</Box>
				) : (
					<Box mt={5}>
						<Empty icon={<GridIcon />} title={t('talentReviews.v2.index.notFound')} small />
					</Box>
				)}
			</Container>
		</>
	);
}
